import { NavLink, Outlet, useSearchParams, useLocation, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import {
  BarChart3,
  Building2,
  CalendarDays,
  GraduationCap,
  Receipt,
  Sparkles,
  Wallet,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { usePermission } from '@/hooks/usePermission';
import { AccessDenied } from '@/components/AccessDenied';

type Segment = 'b2b' | 'b2c';

const TABS_B2B = [
  { id: 'sessions', label: 'Formateurs & Sessions', icon: CalendarDays },
  { id: 'factures', label: 'Factures Clients', icon: Receipt },
  { id: 'dettes', label: 'Dettes Fournisseurs', icon: Wallet },
  { id: 'roi', label: 'ROI & Simulateur', icon: BarChart3 },
  { id: 'inter', label: 'Sessions Inter-entreprises', icon: Building2 },
];

const TABS_B2C = [
  { id: 'sessions', label: 'Sessions B2C', icon: Sparkles },
  { id: 'roi', label: 'ROI & Simulateur', icon: BarChart3 },
  { id: 'inter', label: 'Sessions Inter', icon: Building2 },
];

export function FormationUnifiedLayout(): JSX.Element {
  const canView = usePermission('formation.view');
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();

  const segment: Segment = (params.get('segment') as Segment) === 'b2c' ? 'b2c' : 'b2b';
  const tab = params.get('tab') ?? 'sessions';

  const tabs = segment === 'b2b' ? TABS_B2B : TABS_B2C;

  // Si l'onglet courant n'existe pas dans le segment actif, retomber sur "sessions"
  useEffect(() => {
    if (!tabs.find((t) => t.id === tab)) {
      const next = new URLSearchParams(params);
      next.set('tab', 'sessions');
      setParams(next, { replace: true });
    }
  }, [tab, segment]); // eslint-disable-line react-hooks/exhaustive-deps

  // Si on est sur une sub-route /formation/sessions/:id, ne pas afficher la barre d'onglets (vue détail)
  const isDetailRoute = location.pathname.match(/^\/formation\/sessions\/[^/]+$/);

  const setSegment = (s: Segment): void => {
    const next = new URLSearchParams(params);
    next.set('segment', s);
    next.set('tab', 'sessions');
    setParams(next, { replace: true });
    navigate(`/formation?${next.toString()}`);
  };

  if (!canView) return <AccessDenied feature="formation.view" />;

  if (isDetailRoute) {
    return (
      <div className="flex h-full flex-col">
        <div className="scrollbar-thin flex-1 overflow-y-auto bg-slate-50/40">
          <Outlet />
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <div className="sticky top-0 z-10 border-b border-slate-200 bg-white/80 backdrop-blur">
        {/* Segmented control B2B/B2C */}
        <div className="flex items-center gap-3 px-4 pb-2 pt-3">
          <div className="inline-flex shrink-0 items-center gap-1 rounded-xl bg-slate-100 p-1">
            <button
              type="button"
              onClick={() => setSegment('b2b')}
              className={cn(
                'flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-bold transition-all',
                segment === 'b2b'
                  ? 'bg-white text-violet-700 shadow-sm'
                  : 'text-slate-500 hover:text-slate-700',
              )}
            >
              <GraduationCap className="h-3.5 w-3.5" /> Formation B2B
            </button>
            <button
              type="button"
              onClick={() => setSegment('b2c')}
              className={cn(
                'flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-bold transition-all',
                segment === 'b2c'
                  ? 'bg-white text-rose-600 shadow-sm'
                  : 'text-slate-500 hover:text-slate-700',
              )}
            >
              <Sparkles className="h-3.5 w-3.5" /> Formation B2C
            </button>
          </div>
          <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
            {segment === 'b2b'
              ? 'Vente entreprise · ROI consolidé'
              : 'Séances hebdomadaires · participants'}
          </div>
        </div>

        {/* Tabs internes */}
        <div className="scrollbar-thin flex items-end gap-1 overflow-x-auto px-4">
          {tabs.map((t) => {
            const next = new URLSearchParams(params);
            next.set('tab', t.id);
            const isActive = tab === t.id;
            const accent =
              segment === 'b2b'
                ? 'border-violet-600 text-violet-700'
                : 'border-rose-500 text-rose-600';
            return (
              <NavLink
                key={t.id}
                to={`/formation?${next.toString()}`}
                className={cn(
                  'flex shrink-0 items-center gap-2 whitespace-nowrap border-b-2 px-3 py-3 text-sm font-semibold transition-colors',
                  isActive
                    ? accent
                    : 'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-800',
                )}
              >
                <t.icon className="h-4 w-4" />
                {t.label}
              </NavLink>
            );
          })}
        </div>
      </div>
      <div className="scrollbar-thin flex-1 overflow-y-auto bg-slate-50/40">
        <Outlet context={{ segment, tab }} />
      </div>
    </div>
  );
}
